// Description: Modal that will show the templates Data
// Updated: 29/09/2025
// Author: Simon Franklin

// React
import React from 'react'

// Material UI Components
import { CloseOutlined } from '@mui/icons-material'
import { Modal, Box, Button, Typography } from '@mui/material'

// Components and Functions for the project
import { DataGrid } from '@mui/x-data-grid'
import themeColors from '../../../components/ThemeColors/ThemeColors'
import Titles from '../../../components/Titles/Titles'
import ViewWhatsAppScreen from '../../../components/WhatsAppScreen/ViewWhatsAppScreen'
import StatusIcon from '../../../components/StatusLabel/StatusIcon'
import { notify } from '../../../utils/utils'
import { useNavigate } from 'react-router-dom'

const TemplatesModal = ({ templateOptions, open, onClose, updateFieldValue, field, setError, templateType, formState, currentStep }) => {
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});
    const [viewWhatsAppScreenProps, setViewWhatsAppScreenProps] = React.useState({
        messages: {
            messages: [],
        },
    })
    const activeRows = (templateOptions || []);
    const navigate = useNavigate();

    const handleClose = () => {
        onClose();
        setViewWhatsAppScreenProps({
            messages: {
                messages: [],
            },
        });
        setSelectedRows([]);
    }

    const activeColumns = [
        { headerName: 'Canal', field: 'channel_type', renderCell: (params) => <StatusIcon status={params.value} />, width: 100 },
        { headerName: 'Nome', field: 'title', flex: 1 }
    ]

    React.useEffect(() => {
        if (!selectedRows.length) return;

        const selectedId = selectedRows[0];

        const row = activeRows.find((r) => r.id === selectedId);
        if (!row) return;

        const { messageJson } = row;
        if (!messageJson?.messages) return;

        let type = "";
        let msg = [];

        if (templateType === "SMS") {
            type = "sms";
            msg = messageJson.messages;
        } else if (templateType === "E-mail") {
            type = "e-mail";
            msg = [
                {
                    id: viewWhatsAppScreenProps.messages.messages.length + 1,
                    type: "e-mail",
                    message: messageJson.messages[0].message[0].content,
                    fromMe: true,
                    disabledRemove: true,
                },
            ];
        } else if (templateType.toLowerCase().includes("whatsapp")) {
            type = "whatsapp";
            msg = messageJson.messages;
        }

        if (type) {
            setViewWhatsAppScreenProps((prev) => ({
                ...prev,
                messages: { messages: msg },
                type,
            }));
        }
    }, [selectedRows, activeRows]);

    const handleRedirect = () => {
        sessionStorage.setItem("formState", JSON.stringify(formState))
        sessionStorage.setItem("currentStep", JSON.stringify(currentStep))
        sessionStorage.setItem("creating_campaign", JSON.stringify(true))
        navigate("/modelos/adicionar");
    }

    return (
        <Modal
            onClose={handleClose}
            open={open}
            sx={{ '& .MuiBackdrop-root': { display: "none" } }}
        >
            <Box sx={{ backgroundColor: "#FFF", width: "100vw", height: "100vh", padding: "50px 40px 0px", maxHeight: "100vh", display: "flex", flexDirection: "column" }}>
                <div onClick={handleClose} style={{ cursor: 'pointer' }}>
                    <CloseOutlined
                        sx={{
                            color: themeColors.gray40,
                            display: 'block',
                            position: 'absolute',
                            top: { xs: '10px', sm: '20px' },
                            right: { xs: '10px', sm: '30px' },
                        }}
                    />
                </div>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Titles type="H2" children={"Selecionar Modelo"} color={themeColors.purple500} styles={{ marginBottom: "30px" }} />
                    <Button
                        variant='contained'
                        style={{ color: '#fff', width: '200px', padding: '10px', alignSelf: 'center' }}
                        onClick={() => handleRedirect()}
                    >
                        Criar Modelo
                    </Button>
                </Box>
                <Box sx={{ display: "flex", flex: 1, gap: "20px", maxHeight: "calc(100% - 140px)" }}>
                    <DataGrid
                        rows={activeRows}
                        columns={activeColumns}
                        checkboxSelection
                        rowSelectionModel={selectedRows}
                        disableMultipleRowSelection={true}
                        onRowSelectionModelChange={(ids) => {
                            setError((prev) => ({
                                ...prev,
                                [field]: false
                            }));
                            setSelectedRows(ids)
                        }}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(model) =>
                            setColumnVisibilityModel(model)
                        }
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            density: "comfortable",
                        }}
                        getRowClassName={(params) =>
                            params.row.Visibility === "Oculto" ? "row-oculto" : ""
                        }
                        sx={{
                            backgroundColor: "#FFF",
                            "& .row-oculto": {
                                backgroundColor: "#fffbea",
                            },
                            "& .MuiDataGrid-root": {
                                maxHeight: "calc(100% - 80px)"
                            },
                            flex: 1,
                            maxHeight: "98%"
                        }}
                    />

                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            transform: "scale(0.8)"
                        }}
                    >
                        {
                            templateType === "E-mail" ? (
                                <Box
                                    borderRadius={"12px"}
                                    overflow={"hidden"}
                                    border={"1px solid #A986FF"}
                                    sx={{
                                        backgroundColor: "#fff",
                                        maxWidth: { sm: "561.200px", lg: "628px" },
                                        height: "98%",
                                        width: "100%"
                                    }}
                                >
                                    <Box
                                        zIndex={"2"}
                                        padding={"12px"}
                                        display={"flex"}
                                        gap="12px"
                                        alignItems={"center"}
                                        minWidth={"100%"}
                                        sx={{ backgroundColor: "#8142FC", color: "#fff" }}
                                    >
                                        <StatusIcon
                                            status={"E-mail"}
                                            height={"26px"}
                                            width={"26px"}
                                            color="#fff"
                                        />
                                    </Box>

                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="flex-start"
                                        alignItems="flex-start"
                                        width="100%"
                                        height="100%"
                                        padding="20px"
                                    >
                                        <Typography
                                            sx={{
                                                color: themeColors.gray40,
                                                fontSize: '16px',
                                                textAlign: 'left',
                                                whiteSpace: 'pre-line',
                                                wordBreak: 'break-word',
                                                width: '100%',
                                                mt: 2,
                                            }}
                                        >
                                            {viewWhatsAppScreenProps.messages.messages.length > 0 ? viewWhatsAppScreenProps.messages.messages[0].message : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                
                                    <ViewWhatsAppScreen
                                        {...viewWhatsAppScreenProps}
                                        setViewWhatsAppScreenProps={setViewWhatsAppScreenProps}
                                        viewWhatsAppScreenProps={viewWhatsAppScreenProps}
                                    />
                            )
                        }


                    </Box>
                </Box>

                <div style={{ display: "flex", gap: "16px", minWidth: "100%", justifyContent: "flex-end", paddingBottom: "20px" }}>
                    <Button onClick={() => {
                        setViewWhatsAppScreenProps({
                            messages: {
                                messages: [],
                            },
                        })
                        setSelectedRows([])
                    }} variant="outlined" sx={{ width: "81px", height: "39px", padding: "10px" }}>
                        Cancelar
                    </Button>
                    <Button onClick={() => {
                        if (selectedRows.length === 0) {
                            notify("Alert", "Escolha um modelo antes de avançar", true);
                            return;
                        } else {
                            updateFieldValue(field, selectedRows[0]);
                            handleClose();
                        }
                    }} variant="contained" sx={{ width: "81px", height: "39px", padding: "10px" }}>
                        Selecionar
                    </Button>
                </div>
            </Box>
        </Modal>
    )
}

export default TemplatesModal